/**
 * ExtractionVisualization Component
 *
 * Displays LangExtract's interactive HTML visualization in a sandboxed iframe.
 * Allows users to review source-grounded extractions with the original text
 * highlighted for verification.
 *
 * Features:
 * - Sandboxed iframe for security
 * - Collapse/expand toggle
 * - Open in new tab for full-screen review
 * - Download HTML for offline access
 */

import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

// Icons (inline SVGs to avoid additional dependencies)
const ExternalLinkIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

const EyeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const MapPinIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

// Styled components
const Container = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  margin-top: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border-bottom: 1px solid #e2e8f0;
`;

const TitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: #0ea5e9;
  border-radius: 8px;
  color: white;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
`;

const Subtitle = styled.span`
  font-size: 13px;
  color: #64748b;
  margin-left: 8px;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: #475569;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #f1f5f9;
    border-color: #cbd5e1;
    color: #0f172a;
  }

  &:active {
    transform: translateY(1px);
  }
`;

const IframeContainer = styled.div<{ $collapsed: boolean }>`
  height: ${({ $collapsed }) => ($collapsed ? '0' : '600px')};
  overflow: hidden;
  transition: height 0.3s ease;
`;

const StyledIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  background: #fafafa;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  color: #64748b;
  text-align: center;
`;

const EmptyStateIcon = styled.div`
  width: 48px;
  height: 48px;
  background: #f1f5f9;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  color: #94a3b8;
`;

const EmptyStateTitle = styled.p`
  margin: 0 0 8px 0;
  font-size: 15px;
  font-weight: 500;
  color: #475569;
`;

const EmptyStateDescription = styled.p`
  margin: 0;
  font-size: 13px;
  color: #94a3b8;
`;

// Component props
interface ExtractionVisualizationProps {
  htmlContent?: string | null;
  documentId?: string;
  extractionCount?: number;
  className?: string;
}

/**
 * ExtractionVisualization displays LangExtract's interactive HTML visualization.
 *
 * The visualization shows extracted entities highlighted in their original
 * document context, enabling users to verify extraction accuracy.
 */
const ExtractionVisualization: React.FC<ExtractionVisualizationProps> = ({
  htmlContent,
  documentId,
  extractionCount = 0,
  className,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [collapsed, setCollapsed] = useState(false);

  // Load HTML content into iframe
  useEffect(() => {
    if (iframeRef.current && htmlContent) {
      const doc = iframeRef.current.contentDocument;
      if (doc) {
        doc.open();
        doc.write(htmlContent);
        doc.close();
      }
    }
  }, [htmlContent]);

  // Open visualization in new tab
  const handleOpenInNewTab = () => {
    if (!htmlContent) return;

    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    // Clean up blob URL after a delay
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  // Download visualization as HTML file
  const handleDownload = () => {
    if (!htmlContent) return;

    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `extraction_visualization_${documentId || 'document'}_${Date.now()}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Toggle collapse state
  const toggleCollapse = () => {
    setCollapsed(!collapsed);
  };

  // Render empty state if no content
  if (!htmlContent) {
    return (
      <Container className={className}>
        <Header>
          <TitleSection>
            <IconWrapper>
              <MapPinIcon />
            </IconWrapper>
            <Title>Source-Grounded Extractions</Title>
          </TitleSection>
        </Header>
        <EmptyState>
          <EmptyStateIcon>
            <MapPinIcon />
          </EmptyStateIcon>
          <EmptyStateTitle>No visualization available</EmptyStateTitle>
          <EmptyStateDescription>
            Enable LangExtract extraction to see source-grounded visualization
          </EmptyStateDescription>
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container className={className}>
      <Header>
        <TitleSection>
          <IconWrapper>
            <MapPinIcon />
          </IconWrapper>
          <Title>Source-Grounded Extractions</Title>
          {extractionCount > 0 && (
            <Subtitle>({extractionCount} items)</Subtitle>
          )}
        </TitleSection>
        <Actions>
          <ActionButton onClick={toggleCollapse}>
            {collapsed ? <EyeIcon /> : <EyeOffIcon />}
            {collapsed ? 'Show' : 'Hide'}
          </ActionButton>
          <ActionButton onClick={handleOpenInNewTab}>
            <ExternalLinkIcon />
            Open in New Tab
          </ActionButton>
          <ActionButton onClick={handleDownload}>
            <DownloadIcon />
            Download
          </ActionButton>
        </Actions>
      </Header>
      <IframeContainer $collapsed={collapsed}>
        <StyledIframe
          ref={iframeRef}
          title="Extraction Visualization"
          // Security: allow-scripts for interactivity, but NOT allow-same-origin
          // to prevent potential XSS from accessing parent window
          // Note: This means localStorage/sessionStorage won't work in the visualization
          sandbox="allow-scripts"
        />
      </IframeContainer>
    </Container>
  );
};

export default ExtractionVisualization;
